(() => {
  if (customElements.get('rozest-collection-scroll')) return;

  class RozestCollectionScroll extends HTMLElement {
    connectedCallback() {
      this.grid = this.parentElement.querySelector('#product-grid');
      this.controls = this.querySelector('[data-scroll-controls]');
      this.status = this.querySelector('[data-scroll-status]');
      this.link = this.querySelector('[data-scroll-next]');
      this.fallback = this.querySelector('[data-scroll-pagination]');
      if (!this.grid || !this.controls || !this.status || !this.link || this.initialized) return;
      this.initialized = true;
      this.busy = false;
      this.failed = false;
      this.next = this.dataset.nextUrl || '';
      this.page = Number(this.dataset.page);
      this.context = location.pathname + location.search;
      this.seen = new Set(Array.from(this.grid.children, (item) => item.dataset.productId));
      this.onClick = (event) => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        this.failed = false;
        this.load();
      };
      this.link.addEventListener('click', this.onClick);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting) && !this.failed) this.load();
        }, { rootMargin: '400px 0px' });
      }
      this.fallback.hidden = true;
      this.controls.hidden = false;
      this.render();
      this.observe();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      clearTimeout(this.timeout);
      this.link?.removeEventListener('click', this.onClick);
      this.initialized = false;
    }

    current() {
      // Dawn replaces this element after AJAX filtering and sorting.
      return this.isConnected && this.grid.isConnected &&
        this.context === location.pathname + location.search &&
        !this.closest('.collection').classList.contains('loading');
    }

    observe() {
      this.observer?.disconnect();
      if (this.next && !this.failed && this.isConnected) this.observer?.observe(this);
    }

    render() {
      this.dataset.state = this.busy ? 'loading' : this.failed ? 'error' : this.next ? 'ready' : 'complete';
      this.grid.setAttribute('aria-busy', String(this.busy));
      this.status.textContent = this.busy ? 'Loading more products…' :
        this.failed ? 'Could not load more products. Please try again.' :
        this.next ? 'Scroll for more products' : 'You have viewed all products in this collection.';
      this.link.hidden = !this.next || this.busy;
      this.link.textContent = this.failed ? 'Try again' : 'Load more';
      this.link.setAttribute('href', this.next || '#');
    }

    async load() {
      if (this.busy || !this.next || !this.current()) return;
      this.busy = true;
      this.failed = false;
      this.observer?.disconnect();
      this.render();
      this.controller = new AbortController();
      this.timeout = setTimeout(() => this.controller.abort(), 20000);
      try {
        const url = new URL(this.next, location.href);
        if (url.origin !== location.origin || url.pathname !== location.pathname) throw new Error('Invalid collection URL');
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url.href, { signal: this.controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Collection request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!this.current()) return;
        const incomingGrid = html.querySelector('#product-grid');
        const incomingScroll = html.querySelector('rozest-collection-scroll');
        if (!incomingGrid || !incomingScroll || incomingScroll.dataset.sectionId !== this.dataset.sectionId ||
            Number(incomingScroll.dataset.page) <= this.page) throw new Error('Invalid collection response');
        const items = Array.from(incomingGrid.children).filter((item) => item.matches('li[data-product-id]'));
        if (!items.length) throw new Error('Missing product cards');
        const fragment = document.createDocumentFragment();
        for (const item of items) {
          if (this.seen.has(item.dataset.productId)) continue;
          this.seen.add(item.dataset.productId);
          // Appended cards must not wait for the page's initial animation observer.
          item.classList.add('scroll-trigger--cancel');
          item.querySelectorAll('.scroll-trigger').forEach((child) => child.classList.add('scroll-trigger--cancel'));
          fragment.appendChild(item);
        }
        this.grid.appendChild(fragment);
        this.page = Number(incomingScroll.dataset.page);
        this.dataset.page = String(this.page);
        this.next = incomingScroll.dataset.nextUrl || '';
        this.dataset.nextUrl = this.next;
        if (window.Shopify?.PaymentButton) window.Shopify.PaymentButton.init();
      } catch (error) {
        if (this.current()) this.failed = true;
      } finally {
        clearTimeout(this.timeout);
        this.busy = false;
        if (this.isConnected) {
          this.render();
          if (this.current()) this.observe();
        }
      }
    }
  }

  customElements.define('rozest-collection-scroll', RozestCollectionScroll);
})();
